import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import * as React from 'react';

import { PaginationDirection, ProcessorStatus } from '../../__generated__/globalTypes';
import { CloseIcon } from '../../icons';
import { formatTimeAgo } from '../../utils/formatting';
import Button from '../base/buttons/Button';
import Select from '../base/select/Select';

import AdminSelectUser from './AdminSelectUser';

import type {
  AdminGetAllWebhookCodesQuery,
  AdminGetProcessorStatusesQuery,
  AdminGetProcessorStatusesQueryVariables,
} from './__generated__/ProcessorStatusesPane.gql';

type AdminUpdateProcessorStatusMutationVariables = {
  processorStatusId: string;
  status: ProcessorStatus;
};

type AdminUpdateProcessorStatusMutation = {
  adminUpdateProcessorStatus: {
    id: string;
    status: ProcessorStatus;
  };
};

const PAGE_SIZE = 10;

const UPDATE_PROCESSOR_STATUS = gql`
  mutation AdminUpdateProcessorStatus($processorStatusId: ID!, $status: ProcessorStatus!) {
    adminUpdateProcessorStatus(processorStatusId: $processorStatusId, status: $status) {
      id
      status
    }
  }
`;

const ADMIN_GET_PROCESSOR_STATUSES = gql`
  query AdminGetProcessorStatuses($pagination: PaginationInput!, $filters: ProcessorStatusFilters) {
    adminGetProcessorStatuses(pagination: $pagination, filters: $filters) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          id
          externalBankConnectionId
          webhookCode
          startTime
          finishTime
          status
          errorMessage
          rawJson
          user {
            id
            name
            email
          }
          bankConnection {
            id
            externalId
            bank {
              id
              name
              logoUrl
            }
          }
        }
      }
    }
  }
`;

const ADMIN_GET_ALL_WEBHOOK_CODES = gql`
  query AdminGetAllWebhookCodes {
    adminGetAllWebhookCodes
  }
`;

type StatusFilter = 'ALL' | ProcessorStatus;

type ProcessorStatusType = NonNullable<
  AdminGetProcessorStatusesQuery['adminGetProcessorStatuses']
>['edges'][number]['node'];

interface IBaseProcessorStatusesPaneProps {
  processorStatuses: ProcessorStatusType[];
  statusFilter: StatusFilter;
  userIdFilter: string | null;
  webhookCodeFilter: string | null;
  webhookCodes: string[];
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onStatusFilterChange: (status: StatusFilter) => void;
  onUserIdFilterChange: (userId: string | null) => void;
  onWebhookCodeFilterChange: (webhookCode: string | null) => void;
  onClearFilters: () => void;
  onPageChange: (page: number) => void;
  onResolve: (processorStatusId: string) => Promise<void>;
}

export const BaseProcessorStatusesPane = ({
  processorStatuses,
  statusFilter,
  userIdFilter,
  webhookCodeFilter,
  webhookCodes,
  currentPage,
  hasNextPage,
  hasPreviousPage,
  onStatusFilterChange,
  onUserIdFilterChange,
  onWebhookCodeFilterChange,
  onClearFilters,
  onPageChange,
  onResolve,
}: IBaseProcessorStatusesPaneProps) => {
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const formatDuration = (startTime: string, finishTime: string | null | undefined) => {
    if (!finishTime) {
      return 'Running...';
    }
    const start = new Date(startTime).getTime();
    const finish = new Date(finishTime).getTime();
    const durationMs = finish - start;
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getStatusColor = (status: ProcessorStatus) => {
    switch (status) {
      case ProcessorStatus.Running:
        return 'bg-brand-primary text-brand-primary';
      case ProcessorStatus.Completed:
        return 'bg-success-primary text-success-primary';
      case ProcessorStatus.Failed:
        return 'bg-error-primary text-error-primary';
      case ProcessorStatus.FailedResolved:
        return 'bg-warning-primary text-warning-primary';
      case ProcessorStatus.Skipped:
        return 'bg-secondary text-primary';
      default:
        return 'bg-secondary text-primary';
    }
  };

  const count = processorStatuses.length;

  return (
    <div className="flex h-full w-full flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="text-3xl font-bold">Processor statuses ({count})</div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <label className="text-sm font-medium">Filters:</label>
          {/* User Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">User</label>
            <AdminSelectUser
              selectedUserId={userIdFilter}
              onSelectionChange={onUserIdFilterChange}
              placeholder="Select a user to filter..."
              hideLabel={true}
              className="w-96"
            />
          </div>

          {/* Webhook Code Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Webhook code</label>
            <Select
              placeholder="All webhook codes"
              value={webhookCodeFilter}
              onChange={(key) => {
                if (key) {
                  onWebhookCodeFilterChange(key as string);
                } else {
                  onWebhookCodeFilterChange(null);
                }
              }}
              items={webhookCodes.map((code) => ({
                id: code,
                label: code,
              }))}
              className="w-64"
            >
              {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
            </Select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Status</label>
            <Select
              placeholder="All statuses"
              value={statusFilter === 'ALL' ? null : statusFilter}
              onChange={(key) => {
                if (key) {
                  onStatusFilterChange(key as StatusFilter);
                } else {
                  onStatusFilterChange('ALL');
                }
              }}
              items={[
                { id: ProcessorStatus.Running, label: 'Running' },
                { id: ProcessorStatus.Skipped, label: 'Skipped' },
                { id: ProcessorStatus.Completed, label: 'Completed' },
                { id: ProcessorStatus.Failed, label: 'Failed' },
                { id: ProcessorStatus.FailedResolved, label: 'Failed (resolved)' },
              ]}
              className="w-48"
            >
              {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
            </Select>
          </div>

          {/* Clear Filters Button */}
          {(statusFilter !== 'ALL' || userIdFilter !== null || webhookCodeFilter !== null) && (
            <Button onClick={onClearFilters} color="secondary" size="sm" iconLeading={CloseIcon}>
              Clear filters
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="border-secondary overflow-x-auto rounded-lg border">
        <table className="bg-primary w-full border-collapse text-left text-sm">
          <thead className="bg-secondary">
            <tr>
              <th className="text-primary px-6 py-4 font-medium">Status</th>
              <th className="text-primary px-6 py-4 font-medium">Webhook code</th>
              <th className="text-primary px-6 py-4 font-medium">User</th>
              <th className="text-primary px-6 py-4 font-medium">Bank connection</th>
              <th className="text-primary px-6 py-4 font-medium">Start time</th>
              <th className="text-primary px-6 py-4 font-medium">Duration</th>
              <th className="text-primary px-6 py-4 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-tertiary border-tertiary divide-y border-t">
            {processorStatuses.map((processorStatus) => {
              const isExpanded = expandedRows.has(processorStatus.id);
              const hasRawJson = processorStatus.rawJson != null;

              return (
                <React.Fragment key={processorStatus.id}>
                  <tr className="hover:bg-secondary">
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(processorStatus.status)}`}
                      >
                        {processorStatus.status}
                      </span>
                    </td>
                    <td className="text-primary px-6 py-4 font-mono text-sm">{processorStatus.webhookCode}</td>
                    <td className="text-primary px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-medium">{processorStatus.user?.name || 'Unknown User'}</span>
                        <span className="text-tertiary text-xs">{processorStatus.user?.email || 'No email'}</span>
                        <span className="text-quaternary font-mono text-xs">
                          ID: {processorStatus.user?.id || 'No ID'}
                        </span>
                      </div>
                    </td>
                    <td className="text-primary px-6 py-4">
                      <div className="flex items-center gap-2">
                        {processorStatus.bankConnection?.bank.logoUrl && (
                          <img
                            src={processorStatus.bankConnection.bank.logoUrl}
                            alt={processorStatus.bankConnection.bank.name}
                            className="h-6 w-6 rounded border"
                          />
                        )}
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {processorStatus.bankConnection?.bank.name || 'Unknown Bank'}
                          </span>
                          <span className="text-secondary font-mono text-xs">
                            Connection: {processorStatus.externalBankConnectionId}
                          </span>
                          <span className="text-quaternary font-mono text-xs">
                            Bank ID: {processorStatus.bankConnection?.bank.id || 'No ID'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="text-secondary px-6 py-4">
                      <div className="flex flex-col">
                        <span>{new Date(processorStatus.startTime).toLocaleString()}</span>
                        <span className="text-tertiary text-xs">{formatTimeAgo(processorStatus.startTime)}</span>
                      </div>
                    </td>
                    <td className="text-secondary px-6 py-4">
                      {formatDuration(processorStatus.startTime, processorStatus.finishTime)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2">
                        <Button onClick={() => toggleRow(processorStatus.id)} color="secondary" size="xs">
                          {isExpanded ? 'Hide' : 'Show'} details
                        </Button>
                        {processorStatus.status === ProcessorStatus.Failed && (
                          <Button
                            onClick={() => {
                              void onResolve(processorStatus.id);
                            }}
                            color="primary"
                            size="xs"
                          >
                            Resolve
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={7} className="bg-secondary px-6 py-4">
                        <div className="flex flex-col gap-4">
                          {processorStatus.errorMessage && (
                            <div className="border-primary rounded-lg border p-4">
                              <div className="text-primary mb-2 text-sm font-medium">Error:</div>
                              <div className="bg-primary rounded p-2">
                                <pre className="text-error-primary m-0 max-h-48 overflow-auto whitespace-pre-wrap text-xs">
                                  {processorStatus.errorMessage}
                                </pre>
                              </div>
                            </div>
                          )}
                          {hasRawJson && (
                            <div className="border-primary rounded-lg border p-4">
                              <div className="text-primary mb-2 text-sm font-medium">Raw JSON:</div>
                              <div className="bg-primary rounded p-2">
                                <pre className="m-0 max-h-96 overflow-auto whitespace-pre-wrap text-xs">
                                  {JSON.stringify(processorStatus.rawJson, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}
                          {!processorStatus.errorMessage && !hasRawJson && (
                            <div className="text-tertiary text-sm">No additional details available</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {count > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Button onClick={() => onPageChange(1)} isDisabled={currentPage === 1} color="secondary" size="sm">
              First
            </Button>
            <Button
              onClick={() => onPageChange(currentPage - 1)}
              isDisabled={!hasPreviousPage}
              color="secondary"
              size="sm"
            >
              Previous
            </Button>
            <span className="text-secondary text-sm">Page {currentPage}</span>
            <Button onClick={() => onPageChange(currentPage + 1)} isDisabled={!hasNextPage} color="secondary" size="sm">
              Next
            </Button>
          </div>
          <div className="text-secondary text-sm">
            Showing page {currentPage} • {count} items per page
          </div>
        </div>
      )}

      {/* Empty State */}
      {count === 0 && (
        <div className="text-tertiary flex h-64 items-center justify-center">
          <div className="text-center">
            <div className="text-lg font-medium">No processor statuses found</div>
            <div className="text-sm">Try selecting a different filter</div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProcessorStatusesPane = () => {
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('ALL');
  const [userIdFilter, setUserIdFilter] = React.useState<string | null>(null);
  const [webhookCodeFilter, setWebhookCodeFilter] = React.useState<string | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentCursor, setCurrentCursor] = React.useState<string | undefined>(undefined);
  const [direction, setDirection] = React.useState<PaginationDirection>(PaginationDirection.Forward);

  const [updateProcessorStatus] = useMutation<
    AdminUpdateProcessorStatusMutation,
    AdminUpdateProcessorStatusMutationVariables
  >(UPDATE_PROCESSOR_STATUS);

  // Fetch all webhook codes for the filter dropdown
  const { data: webhookCodesData } = useQuery<AdminGetAllWebhookCodesQuery>(ADMIN_GET_ALL_WEBHOOK_CODES);
  const webhookCodes = webhookCodesData?.adminGetAllWebhookCodes ?? [];

  // Build filters object
  const buildFilters = (status: StatusFilter, userId: string | null, webhookCode: string | null) => {
    const filters: { status?: ProcessorStatus; userId?: string; webhookCode?: string } = {};
    if (status !== 'ALL') {
      filters.status = status;
    }
    if (userId) {
      filters.userId = userId;
    }
    if (webhookCode) {
      filters.webhookCode = webhookCode;
    }
    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  const [executeQuery, { data, loading }] = useLazyQuery<
    AdminGetProcessorStatusesQuery,
    AdminGetProcessorStatusesQueryVariables
  >(ADMIN_GET_PROCESSOR_STATUSES);

  const processorStatuses = data?.adminGetProcessorStatuses?.edges?.map((edge) => edge.node) ?? [];
  const pageInfo = data?.adminGetProcessorStatuses?.pageInfo ?? null;
  const hasNextPage = data?.adminGetProcessorStatuses?.pageInfo?.hasNextPage ?? false;
  const hasPreviousPage = data?.adminGetProcessorStatuses?.pageInfo?.hasPreviousPage ?? false;

  // Execute query function - use currentCursor and direction
  const executeQueryWithCurrentParams = React.useCallback(() => {
    void executeQuery({
      variables: {
        pagination: {
          cursor: currentCursor,
          limit: PAGE_SIZE,
          direction: direction,
        },
        filters: buildFilters(statusFilter, userIdFilter, webhookCodeFilter),
      },
    });
  }, [executeQuery, currentCursor, direction, statusFilter, userIdFilter, webhookCodeFilter]);

  // Execute query on mount and when filters/page change
  React.useEffect(() => {
    executeQueryWithCurrentParams();
  }, [executeQueryWithCurrentParams]);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  const handleStatusFilterChange = (status: StatusFilter) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filter changes
    setCurrentCursor(undefined); // Reset cursor when filter changes
    setDirection(PaginationDirection.Forward); // Reset direction when filter changes
  };

  const handleUserIdFilterChange = (userId: string | null) => {
    setUserIdFilter(userId);
    setCurrentPage(1); // Reset to first page when filter changes
    setCurrentCursor(undefined); // Reset cursor when filter changes
    setDirection(PaginationDirection.Forward); // Reset direction when filter changes
  };

  const handleWebhookCodeFilterChange = (webhookCode: string | null) => {
    setWebhookCodeFilter(webhookCode);
    setCurrentPage(1); // Reset to first page when filter changes
    setCurrentCursor(undefined); // Reset cursor when filter changes
    setDirection(PaginationDirection.Forward); // Reset direction when filter changes
  };

  const handleClearFilters = () => {
    setStatusFilter('ALL');
    setUserIdFilter(null);
    setWebhookCodeFilter(null);
    setCurrentPage(1);
    setCurrentCursor(undefined);
    setDirection(PaginationDirection.Forward);
  };

  const handlePageChange = (page: number) => {
    if (page === 1) {
      // Going to first page - reset cursor and use forward direction
      setCurrentCursor(undefined);
      setDirection(PaginationDirection.Forward);
    } else if (page > currentPage) {
      // Going forward - use endCursor with FORWARD direction
      setCurrentCursor(pageInfo?.endCursor ?? undefined);
      setDirection(PaginationDirection.Forward);
    } else if (page < currentPage) {
      // Going backward - use startCursor with BACKWARD direction
      setCurrentCursor(pageInfo?.startCursor ?? undefined);
      setDirection(PaginationDirection.Backward);
    }
    setCurrentPage(page);
  };

  const handleResolve = async (processorStatusId: string) => {
    try {
      await updateProcessorStatus({
        variables: {
          processorStatusId,
          status: ProcessorStatus.FailedResolved,
        },
      });
      // Refetch the query to update the UI
      executeQueryWithCurrentParams();
    } catch {
      // Error is handled by Apollo error link and sent to Sentry
    }
  };

  return (
    <BaseProcessorStatusesPane
      processorStatuses={processorStatuses}
      statusFilter={statusFilter}
      userIdFilter={userIdFilter}
      webhookCodeFilter={webhookCodeFilter}
      webhookCodes={webhookCodes}
      currentPage={currentPage}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      onStatusFilterChange={handleStatusFilterChange}
      onUserIdFilterChange={handleUserIdFilterChange}
      onWebhookCodeFilterChange={handleWebhookCodeFilterChange}
      onClearFilters={handleClearFilters}
      onPageChange={handlePageChange}
      onResolve={handleResolve}
    />
  );
};

export default ProcessorStatusesPane;
